import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, Handbag } from "@phosphor-icons/react";
import { useSelection } from "./SelectionContext";
import { useCart } from "../../cart";

const LINKS = [
  { label: "Shop", to: "/shop" },
  { label: "Product Collections", to: "/collections" },
  { label: "Bespoke Customisation", to: "/bespoke" },
  { label: "Quality & Service", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { items } = useSelection();
  const { count } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-[#F2EBDD]/75 backdrop-blur-2xl border-b border-black/10" : "border-b border-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-16 h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" aria-label="Emporium London — home" className="group">
          <img src="/emporium-logo.png" alt="Emporium London" className="h-8 sm:h-9 w-auto transition-opacity duration-500 group-hover:opacity-80" />
        </Link>

        <nav aria-label="Primary" className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.slice(1)}`}
              className={({ isActive }) =>
                `font-mono text-[11px] tracking-[0.2em] uppercase transition-colors duration-300 ${
                  isActive ? "text-[var(--text-primary)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-enquiry-cta"
            className="hidden sm:inline-flex items-center gap-2 border border-black/20 px-5 h-11 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-[var(--text-primary)] hover:text-[var(--bg-primary)] transition-colors duration-500"
          >
            Request a Selection
            {items.length > 0 && (
              <span data-testid="nav-selection-count" className="inline-flex items-center justify-center min-w-5 h-5 px-1 bg-[var(--gold)] text-[#F2EBDD] text-[10px] rounded-full">
                {items.length}
              </span>
            )}
          </Link>

          <Link to="/cart" data-testid="nav-cart" aria-label="Basket" className="relative inline-flex items-center justify-center h-11 w-11 text-[var(--text-primary)] hover:text-[var(--brass)] transition-colors">
            <Handbag size={22} />
            {count > 0 && <span data-testid="nav-cart-count" className="absolute top-1 right-0 inline-flex items-center justify-center min-w-4 h-4 px-1 bg-[var(--text-primary)] text-[var(--bg-primary)] text-[9px] rounded-full">{count}</span>}
          </Link>

          <button data-testid="mobile-menu-toggle" aria-label="Toggle menu" onClick={() => setOpen((v) => !v)} className="lg:hidden text-[var(--text-primary)]">
            {open ? <X size={26} /> : <List size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            aria-label="Mobile"
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
            className="lg:hidden overflow-hidden bg-[#F2EBDD]/97 backdrop-blur-2xl border-b border-black/10"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {[...LINKS, { label: "Request a Selection", to: "/contact" }].map((l) => (
                <button
                  key={l.to}
                  data-testid={`mobile-nav-${l.to.slice(1)}`}
                  onClick={() => { setOpen(false); navigate(l.to); }}
                  className="text-left font-serif text-3xl text-[var(--text-primary)]"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
